import React, { useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Collapse,
  Stack,
  Avatar,
  IconButton,
  Chip,
} from '@mui/material';
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  EmojiEvents as TrophyIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { COLORS, SECTION_STYLES } from './MAMCStyles';

// Enhance table styles
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  color: COLORS.text.primary,
  borderBottom: `1px solid ${COLORS.background.light}`,
  padding: theme.spacing(1.5),
  '&.header': {
    color: COLORS.text.accent,
    fontWeight: 'bold',
    borderBottom: `2px solid ${COLORS.secondary}`,
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(218,165,32,0.1)',
    '& > td': {
      color: COLORS.text.accent,
    },
  },
  '&.expanded': {
    backgroundColor: 'rgba(218,165,32,0.05)',
  },
}));

const GroupStandings = ({ group, teams, teamStats, matches, scores }) => {
  const sortedTeams = [...teams].sort((a, b) => {
    const statsA = teamStats[a.id] || { points: 0, goalDifference: 0, goalsFor: 0 };
    const statsB = teamStats[b.id] || { points: 0, goalDifference: 0, goalsFor: 0 };

    if (statsB.points !== statsA.points) {
      return statsB.points - statsA.points;
    }
    if (statsB.goalDifference !== statsA.goalDifference) {
      return statsB.goalDifference - statsA.goalDifference;
    }
    return statsB.goalsFor - statsA.goalsFor;
  });

  const [expandedTeam, setExpandedTeam] = useState(null);

  // Helper function to get ongoing match for a team
  const getOngoingMatch = teamId => {
    if (!matches || !scores) return null;

    // Only consider group stage matches (matches without a type or type === 'Group Stage')
    const ongoingMatch = matches.find(
      match =>
        match.ongoing &&
        (match.home_team_id === teamId || match.away_team_id === teamId) &&
        (!match.type || match.type === 'Group stage')
    );

    if (!ongoingMatch) return null;

    const score = scores.find(s => s.match_id === ongoingMatch.id);
    const isHomeTeam = ongoingMatch.home_team_id === teamId;
    const opposingTeamId = isHomeTeam ? ongoingMatch.away_team_id : ongoingMatch.home_team_id;
    const opposingTeam = teams.find(t => t.id === opposingTeamId);

    return {
      match: ongoingMatch,
      score,
      isHomeTeam,
      opposingTeam,
      homeTeamScore: score?.home_team_score || 0,
      awayTeamScore: score?.away_team_score || 0,
    };
  };

  const handleRowClick = teamId => {
    setExpandedTeam(expandedTeam === teamId ? null : teamId);
  };

  const formatDate = dateString => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const renderMatchHistory = (teamId, matchHistory) => {
    return (
      <Collapse in={expandedTeam === teamId} timeout="auto" unmountOnExit>
        <Box sx={{ p: 2, bgcolor: 'rgba(0, 0, 0, 0.02)' }}>
          <Typography variant="subtitle2" sx={{ mb: 2, color: '#ffffff' }}>
            Last 5 Matches
          </Typography>
          {matchHistory.length > 0 ? (
            <Stack spacing={1}>
              {matchHistory.map((match, index) => {
                const teamScore = match.isHomeTeam ? match.homeTeamScore : match.awayTeamScore;
                const opponentScore = match.isHomeTeam ? match.awayTeamScore : match.homeTeamScore;
                const result =
                  teamScore > opponentScore ? 'W' : teamScore < opponentScore ? 'L' : 'D';
                const resultColor =
                  result === 'W' ? '#4CAF50' : result === 'L' ? '#f44336' : '#FFA726';

                const homeTeam = teams.find(t => t.id === match.homeTeamId);
                const awayTeam = teams.find(t => t.id === match.awayTeamId);

                return (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      p: 1.5,
                      borderRadius: 1,
                      bgcolor: '#000000',
                    }}
                  >
                    <Typography
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        bgcolor: resultColor,
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.75rem',
                        fontWeight: 'bold',
                        flexShrink: 0,
                      }}
                    >
                      {result}
                    </Typography>
                    <Typography variant="body2" sx={{ flex: 1, color: '#ffffff' }}>
                      {formatDate(match.date)}
                    </Typography>
                    <Box
                      sx={{
                        flex: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ color: '#ffffff', textAlign: 'right', flex: 1 }}
                      >
                        {homeTeam?.team_name || 'Unknown'}
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          bgcolor: 'rgba(218,165,32,0.15)',
                          borderRadius: 1,
                          px: 1,
                          py: 0.5,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 'bold',
                            color: '#DAA520',
                            minWidth: '40px',
                            textAlign: 'center',
                          }}
                        >
                          {match.homeTeamScore} - {match.awayTeamScore}
                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{ color: '#ffffff', textAlign: 'left', flex: 1 }}
                      >
                        {awayTeam?.team_name || 'Unknown'}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          ) : (
            <Typography variant="body2" color="#ffffff" sx={{ textAlign: 'center' }}>
              No matches played yet
            </Typography>
          )}
        </Box>
      </Collapse>
    );
  };

  return (
    <Box
      sx={{
        ...SECTION_STYLES.groupStandings,
        p: { xs: 2, sm: 3 },
        mb: { xs: 2, sm: 3, md: 4 },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mb: 3,
          pb: 2,
          borderBottom: `2px solid ${COLORS.background.light}`,
        }}
      >
        <TrophyIcon
          sx={{
            color: COLORS.secondary,
            fontSize: 28,
            filter: 'drop-shadow(0 2px 4px rgba(218,165,32,0.3))',
          }}
        />
        <Typography
          variant="h6"
          sx={{
            color: COLORS.text.accent,
            fontWeight: 'bold',
            textShadow: '0 2px 4px rgba(0,0,0,0.3)',
            letterSpacing: '1px',
          }}
        >
          Group {group}
        </Typography>
      </Box>

      <Box sx={{ overflowX: 'auto', width: '100%', WebkitOverflowScrolling: 'touch' }}>
        <Table sx={{ minWidth: { xs: 650, sm: 800 } }}>
          <TableHead>
            <TableRow>
              <StyledTableCell className="header">Team</StyledTableCell>
              <StyledTableCell className="header" align="center">
                P
              </StyledTableCell>
              <StyledTableCell className="header" align="center">
                W
              </StyledTableCell>
              <StyledTableCell className="header" align="center">
                D
              </StyledTableCell>
              <StyledTableCell className="header" align="center">
                L
              </StyledTableCell>
              <StyledTableCell className="header" align="center">
                GF
              </StyledTableCell>
              <StyledTableCell className="header" align="center">
                GA
              </StyledTableCell>
              <StyledTableCell className="header" align="center">
                GD
              </StyledTableCell>
              <StyledTableCell className="header" align="center">
                Pts
              </StyledTableCell>
              <StyledTableCell className="header" />
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedTeams.map((team, index) => {
              const stats = teamStats[team.id] || {
                matchesPlayed: 0,
                wins: 0,
                draws: 0,
                losses: 0,
                goalsFor: 0,
                goalsAgainst: 0,
                goalDifference: 0,
                points: 0,
                matchHistory: [],
              };

              // Get ongoing match for this team
              const ongoingMatch = getOngoingMatch(team.id);

              return (
                <React.Fragment key={team.id}>
                  <StyledTableRow
                    className={expandedTeam === team.id ? 'expanded' : ''}
                    onClick={() => handleRowClick(team.id)}
                    sx={{
                      cursor: 'pointer',
                      position: 'relative',
                      '&::after':
                        index === 0
                          ? {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              height: '2px',
                              background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary}, ${COLORS.accent})`,
                            }
                          : {},
                    }}
                  >
                    <StyledTableCell>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          minWidth: '150px',
                        }}
                      >
                        <Avatar
                          sx={{
                            width: { xs: 24, sm: 28 },
                            height: { xs: 24, sm: 28 },
                            bgcolor: COLORS.secondary,
                            flexShrink: 0,
                            fontSize: { xs: '0.8rem', sm: '0.9rem' },
                            fontWeight: 'bold',
                          }}
                        >
                          {team.team_name.charAt(0)}
                        </Avatar>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          <Typography
                            sx={{
                              color: COLORS.text.primary,
                              flex: 1,
                              fontSize: { xs: '0.875rem', sm: '1rem' },
                              fontWeight: expandedTeam === team.id ? 'bold' : 'normal',
                            }}
                          >
                            {team.team_name}
                          </Typography>

                          {/* Last 5 Games Form Indicator */}
                          {stats.matchHistory && stats.matchHistory.length > 0 && (
                            <Box
                              sx={{
                                display: 'flex',
                                gap: 0.3,
                                alignItems: 'center',
                              }}
                            >
                              {stats.matchHistory.slice(0, 5).map((match, idx) => {
                                const teamScore = match.isHomeTeam
                                  ? match.homeTeamScore
                                  : match.awayTeamScore;
                                const opponentScore = match.isHomeTeam
                                  ? match.awayTeamScore
                                  : match.homeTeamScore;
                                const result =
                                  teamScore > opponentScore
                                    ? 'W'
                                    : teamScore < opponentScore
                                      ? 'L'
                                      : 'D';
                                const resultColor =
                                  result === 'W'
                                    ? '#4CAF50'
                                    : result === 'L'
                                      ? '#F44336'
                                      : '#FF9800';

                                return (
                                  <Box
                                    key={idx}
                                    sx={{
                                      width: 16,
                                      height: 16,
                                      borderRadius: '50%',
                                      backgroundColor: resultColor,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: '0.6rem',
                                      fontWeight: 'bold',
                                      color: 'white',
                                      textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                                    }}
                                    title={`${result === 'W' ? 'Win' : result === 'L' ? 'Loss' : 'Draw'} - ${teamScore}-${opponentScore}`}
                                  >
                                    {result}
                                  </Box>
                                );
                              })}
                            </Box>
                          )}

                          {/* Show ongoing match if exists */}
                          {ongoingMatch && (
                            <Chip
                              size="small"
                              label={
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontWeight: 'bold',
                                    fontSize: '0.6rem',
                                    lineHeight: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                >
                                  {ongoingMatch.isHomeTeam
                                    ? `${ongoingMatch.homeTeamScore}-${ongoingMatch.awayTeamScore}`
                                    : `${ongoingMatch.awayTeamScore}-${ongoingMatch.homeTeamScore}`}
                                </Typography>
                              }
                              sx={{
                                height: '16px',
                                minWidth: '30px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: (() => {
                                  // Determine if team is winning, losing or drawing
                                  const teamScore = ongoingMatch.isHomeTeam
                                    ? ongoingMatch.homeTeamScore
                                    : ongoingMatch.awayTeamScore;
                                  const opponentScore = ongoingMatch.isHomeTeam
                                    ? ongoingMatch.awayTeamScore
                                    : ongoingMatch.homeTeamScore;

                                  if (teamScore > opponentScore) return '#4CAF50'; // Green for winning
                                  if (teamScore < opponentScore) return '#F44336'; // Red for losing
                                  return '#9E9E9E'; // Grey for draw
                                })(),
                                border: (() => {
                                  // Determine if team is winning, losing or drawing
                                  const teamScore = ongoingMatch.isHomeTeam
                                    ? ongoingMatch.homeTeamScore
                                    : ongoingMatch.awayTeamScore;
                                  const opponentScore = ongoingMatch.isHomeTeam
                                    ? ongoingMatch.awayTeamScore
                                    : ongoingMatch.homeTeamScore;

                                  if (teamScore > opponentScore) return '1px solid #4CAF50'; // Green for winning
                                  if (teamScore < opponentScore) return '1px solid #F44336'; // Red for losing
                                  return '1px solid #9E9E9E'; // Grey for draw
                                })(),
                                '& .MuiChip-label': {
                                  px: 0.5,
                                  py: 0,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: '#FFFFFF', // White text
                                },
                              }}
                            />
                          )}
                        </Box>
                      </Box>
                    </StyledTableCell>
                    <StyledTableCell align="center">{stats.matchesPlayed}</StyledTableCell>
                    <StyledTableCell align="center">{stats.wins}</StyledTableCell>
                    <StyledTableCell align="center">{stats.draws}</StyledTableCell>
                    <StyledTableCell align="center">{stats.losses}</StyledTableCell>
                    <StyledTableCell align="center">{stats.goalsFor}</StyledTableCell>
                    <StyledTableCell align="center">{stats.goalsAgainst}</StyledTableCell>
                    <StyledTableCell
                      align="center"
                      sx={{
                        color:
                          stats.goalDifference > 0
                            ? COLORS.success
                            : stats.goalDifference < 0
                              ? COLORS.error
                              : COLORS.text.primary,
                        fontWeight: 'bold',
                      }}
                    >
                      {stats.goalDifference > 0 ? '+' : ''}
                      {stats.goalDifference}
                    </StyledTableCell>
                    <StyledTableCell
                      align="center"
                      sx={{
                        color: COLORS.text.accent,
                        fontWeight: 'bold',
                        fontSize: { xs: '0.9rem', sm: '1rem' },
                      }}
                    >
                      {stats.points}
                    </StyledTableCell>
                    <StyledTableCell>
                      <IconButton
                        size="small"
                        onClick={e => {
                          e.stopPropagation();
                          handleRowClick(team.id);
                        }}
                        sx={{
                          color: COLORS.text.accent,
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            color: COLORS.secondary,
                            transform: 'scale(1.1)',
                          },
                        }}
                      >
                        {expandedTeam === team.id ? (
                          <KeyboardArrowUpIcon />
                        ) : (
                          <KeyboardArrowDownIcon />
                        )}
                      </IconButton>
                    </StyledTableCell>
                  </StyledTableRow>
                  <TableRow>
                    <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={10}>
                      {renderMatchHistory(team.id, stats.matchHistory)}
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
};

export default GroupStandings;
